import React, { Fragment, useContext } from "react";
import {
    TrashIcon,
    CloudDownloadIcon,
    CloudUploadIcon,
    TagIcon,
    RefreshIcon,
    SupportIcon,
    FolderIcon,
    CollectionIcon,
    DocumentDownloadIcon,
} from "@heroicons/react/outline";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import ToolbarMenu, {
    ToolbarMenuItems,
    ToolbarMenuItem,
} from "components/Toolbar/ToolbarMenu";
import { useContextualHelp } from "hooks/Help";
import { apiPost, apiDel, apiPut, messageFromException } from "helpers/Request";
import { SQL_DATE_TIME_FORMAT, fromNow } from "helpers/Date";
import { useAuth } from "hooks/Auth";
import { DashboardContext } from "context/Dashboard";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import {
    onRequestTag,
    onRequestWorkflow,
    onRequestCollection,
} from "./ToolbarActions";
import { getCollections } from "redux/actions/content-flow";

export default function WrappedToolbar(props) {
    const { loadContent, selectedItems } = useContext(DashboardContext);
    const contextualHelp = useContextualHelp();
    const auth = useAuth();
    const dispatch = useDispatch();
    const { workflows } = useSelector((state) => state?.contentFlow);

    const onCollectionCreated = () => {
        dispatch(getCollections());
    };

    const onRequestPublish = () => {
        const data = {
            forced: true,
            published_at: fromNow().toUTC().toFormat(SQL_DATE_TIME_FORMAT),
        };

        onRequestUpdate(data);
    };
    const onRequestUnpublish = () => {
        const data = {
            forced: true,
            published_at: null,
        };

        onRequestUpdate(data);
    };

    const onRequestUpdate = (data) => {
        const id = toast.loading("Updating...");

        const promises = selectedItems.map((uuid) =>
            apiPut(`/v4/content/${uuid}`, auth.publication, data)
        );
        Promise.all(promises)
            .then(() => {
                toast.success("Updated!", { id });
                loadContent();
            })
            .catch((err) => {
                modal.error({
                    title: "Unable to update selected items",
                    children: messageFromException(err),
                });
                toast.dismiss();
            });
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Items",
            children: (
                <p>
                    Are you sure you want to delete the selected items? This
                    cannot be undone.
                </p>
            ),
            buttons: ({ disabled }) => (
                <Fragment>
                    <DangerButton
                        disabled={disabled}
                        onClick={() => {
                            const id = toast.loading("Deleting...");
                            modal.update({ disabled: true });
                            handleDelete().finally(() => {
                                toast.dismiss(id);
                                modal.dismiss();
                            });
                        }}
                    >
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/content/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Content deleted");
                loadContent();
            })
            .catch((err) => {
                console.error(err);
                modal.update({
                    title: "Unable to delete those items",
                    children: (
                        <Fragment>
                            CEO was unable to delete those items, usually this
                            is because you do not have permission to delete.
                        </Fragment>
                    ),
                });
            });
    };

    const exportAs = (type) => {
        const url = "/v4/export/as/" + type;

        apiPost(url, auth.publication, selectedItems, true, null, {
            responseType: "arraybuffer",
        })
            .then((resp) => {
                if (!window) {
                    return;
                }
                let ext = "zip";
                let data = resp;

                const fileName = `ceo-export.${ext}`;
                // create the data url, inject, click and remove
                const earl = window.URL.createObjectURL(
                    new Blob([data], { type: "application/zip" })
                );
                const tempLink = document.createElement("a");
                tempLink.href = earl;
                tempLink.setAttribute("download", fileName);
                tempLink.setAttribute("target", "_blank");
                document.body.appendChild(tempLink);
                tempLink.click();
                document.body.removeChild(tempLink);
                toast.success("Content exported");
            })
            .catch((err) => {
                toast.error(err.message);
            });
    };

    return (
        <div className="sticky top-0 z-10">
            <Toolbar>
                <ToolbarGroup>
                    <ToolbarButton
                        icon={<RefreshIcon />}
                        onClick={loadContent}
                    />
                </ToolbarGroup>
                <ToolbarGroup className="flex-grow">
                    <ToolbarButton
                        icon={<TrashIcon />}
                        disabled={!selectedItems.length}
                        onClick={onRequestDelete}
                    >
                        Delete
                    </ToolbarButton>
                    <ToolbarButton
                        icon={<CloudUploadIcon />}
                        disabled={!selectedItems.length}
                        onClick={onRequestPublish}
                    >
                        Publish
                    </ToolbarButton>
                    <ToolbarButton
                        icon={<CloudDownloadIcon />}
                        disabled={!selectedItems.length}
                        onClick={onRequestUnpublish}
                    >
                        Unpublish
                    </ToolbarButton>
                    <ToolbarButton
                        icon={<TagIcon />}
                        disabled={!selectedItems.length}
                        onClick={() => {
                            onRequestTag({ loadContent, selectedItems, auth });
                        }}
                    >
                        Tag
                    </ToolbarButton>
                    <ToolbarButton
                        icon={<FolderIcon />}
                        disabled={!selectedItems.length}
                        onClick={() => {
                            onRequestWorkflow({
                                loadContent,
                                selectedItems,
                                workflows,
                                auth,
                            });
                        }}
                    >
                        Workflow
                    </ToolbarButton>
                    <ToolbarButton
                        icon={<CollectionIcon />}
                        disabled={!selectedItems.length}
                        onClick={() => {
                            onRequestCollection(
                                selectedItems,
                                auth,
                                onCollectionCreated
                            );
                        }}
                    >
                        Collection
                    </ToolbarButton>
                    <ToolbarMenu
                        icon={<DocumentDownloadIcon />}
                        disabled={!selectedItems.length}
                        label="Export"
                    >
                        <ToolbarMenuItems>
                            <ToolbarMenuItem onClick={() => exportAs("html")}>
                                Plain HTML
                            </ToolbarMenuItem>
                            <ToolbarMenuItem onClick={() => exportAs("text")}>
                                Plain Text
                            </ToolbarMenuItem>
                            <ToolbarMenuItem onClick={() => exportAs("icml")}>
                                InCopy/InDesign
                            </ToolbarMenuItem>
                            <ToolbarMenuItem
                                onClick={() => exportAs("simplifiedxml")}
                            >
                                Simplified XML
                            </ToolbarMenuItem>
                            <ToolbarMenuItem
                                onClick={() => exportAs("applenews")}
                            >
                                Apple News Format
                            </ToolbarMenuItem>
                            <ToolbarMenuItem onClick={() => exportAs("ceo")}>
                                CEO Export Format
                            </ToolbarMenuItem>
                            <ToolbarMenuItem
                                onClick={() => exportAs("ceointerchange")}
                            >
                                CEO API Interchange Format
                            </ToolbarMenuItem>
                        </ToolbarMenuItems>
                    </ToolbarMenu>
                    <ToolbarButton
                        className="ml-auto text-green-600"
                        icon={<SupportIcon />}
                        onClick={() => {
                            contextualHelp.showContextualHelp(
                                "/managing-content.html"
                            );
                        }}
                    >
                        Help
                    </ToolbarButton>
                </ToolbarGroup>
            </Toolbar>
        </div>
    );
}
